import React, { useEffect, useMemo, useState } from "react";
import { Button, Collapse } from "react-bootstrap";
import { useSubheader } from "../../../../_metronic/layout";
import { CheckInTabs } from "../Components/CheckInTabs";
import { KioskDayPicker } from "../Components/KioskDayPicker";
import { useKioskDay } from "../Components/useKioskDay";
import {
  KIOSK_TYPED_MARK,
  STUDENT_STATES,
  fetchAttendance,
  formatScanTime,
  studentName,
  studentState,
} from "../Components/attendanceCrud";
// `formatTime` is for the batch's own clock columns (`start_time`, "10:00:00")
// — plain wall-clock strings with no date and no offset. The scan timestamps
// go through `formatScanTime` instead; see its comment for why they must not
// share a formatter with anything Date-based.
import { formatTime } from "../Components/badgesCrud";

const FORBIDDEN =
  "You do not have permission to view attendance for this branch. Ask an Admin or Branch Head.";
const FALLBACK = "Could not load attendance for that day.";

/**
 * How each of the three states is drawn, in one table so the rendering can be
 * read against the contract in a single glance.
 *
 * The `time` column is the point of the whole screen. A student the coach
 * marked has no arrival time and never had one — there is nothing to look up,
 * nothing still loading, and nothing to infer from the session's start time.
 * Rendering that as an empty cell would say "bug" to every coach who saw it,
 * and this is not a rare case: 181,374 of the 181,398 attendance rows in the
 * live database were marked by a coach, so the blank would be the *normal*
 * appearance of a working screen. So it states itself in words instead.
 */
const STATE_STYLES = {
  [STUDENT_STATES.SCANNED]: {
    label: "Present",
    badge: "label label-lg label-light-success label-inline",
    note: "Checked in at the door",
  },
  [STUDENT_STATES.COACH_MARKED]: {
    label: "Present",
    badge: "label label-lg label-light-primary label-inline",
    note: "Marked by coach",
  },
  [STUDENT_STATES.UNMARKED]: {
    label: "Unmarked",
    badge: "label label-lg label-light-warning label-inline",
    note: "Not recorded",
  },
};

function sessionKey(session, index) {
  return session.batch_id === null || session.batch_id === undefined
    ? `row-${index}`
    : String(session.batch_id);
}

function sessionLabel(session) {
  const time = `${formatTime(session.start_time)}–${formatTime(
    session.end_time
  )}`;
  const courses = Array.isArray(session.course_codes)
    ? session.course_codes.filter(Boolean).join(", ")
    : "";
  return [session.day, time, session.program_name, courses]
    .filter(Boolean)
    .join("  ·  ");
}

/**
 * Per-session tallies. Door check-ins are counted separately from the present,
 * and typed ones (number or name, no badge) separately again.
 */
function tally(students) {
  const counts = { present: 0, absent: 0, unmarked: 0, scanned: 0, typed: 0 };
  students.forEach((student) => {
    const state = studentState(student);
    if (state === STUDENT_STATES.UNMARKED) counts.unmarked += 1;
    else if (student.attendance === "absent") counts.absent += 1;
    else counts.present += 1;
    if (state === STUDENT_STATES.SCANNED) {
      counts.scanned += 1;
      if (student.marked_via === KIOSK_TYPED_MARK) counts.typed += 1;
    }
  });
  return counts;
}

export function AttendanceList() {
  const subheader = useSubheader();
  subheader.setTitle("Check-in Attendance");

  const state = useKioskDay(fetchAttendance, FORBIDDEN, FALLBACK);
  const { rows: sessions, date, isLoading, errorMessage, hasLoaded } = state;

  const [expanded, setExpanded] = useState(() => new Set());

  // A roster left open from one branch or day must not stay open over another
  // one's sessions — batch ids from two branches can collide by coincidence.
  useEffect(() => {
    setExpanded(new Set());
  }, [state.branchId, date]);

  const orderedSessions = useMemo(
    () =>
      [...sessions].sort((a, b) =>
        (a.start_time || "").localeCompare(b.start_time || "")
      ),
    [sessions]
  );

  // A single session collapsed behind a click is just a worse table.
  useEffect(() => {
    if (orderedSessions.length === 1) {
      setExpanded(new Set([sessionKey(orderedSessions[0], 0)]));
    }
  }, [orderedSessions]);

  const dayTotals = useMemo(() => {
    const totals = { present: 0, absent: 0, unmarked: 0, scanned: 0, typed: 0 };
    orderedSessions.forEach((session) => {
      const counts = tally(
        Array.isArray(session.students) ? session.students : []
      );
      totals.present += counts.present;
      totals.absent += counts.absent;
      totals.unmarked += counts.unmarked;
      totals.scanned += counts.scanned;
      totals.typed += counts.typed;
    });
    return totals;
  }, [orderedSessions]);

  const toggle = (key) =>
    setExpanded((previous) => {
      const next = new Set(previous);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const allKeys = orderedSessions.map(sessionKey);
  const allOpen =
    allKeys.length > 0 && allKeys.every((key) => expanded.has(key));

  return (
    <div className="card card-custom">
      <div className="card-header border-0 py-5">
        <h3 className="card-title align-items-start flex-column">
          <span className="card-label font-weight-bolder text-dark">
            Attendance
          </span>
          <span className="text-muted mt-3 font-weight-bold font-size-sm">
            Sessions at{" "}
            {state.currentBranch && state.currentBranch.branch_name
              ? state.currentBranch.branch_name
              : "this branch"}
            , and who was marked in each.
          </span>
        </h3>
      </div>

      <div className="card-body pt-0">
        <CheckInTabs />

        <KioskDayPicker
          label="Date"
          date={date}
          setDate={state.setDate}
          onRefresh={state.refresh}
          isLoading={isLoading}
        />

        {errorMessage && (
          <div
            className="mb-5 alert alert-custom alert-light-danger"
            role="alert"
          >
            <div className="alert-text">{errorMessage}</div>
          </div>
        )}

        {!state.branchReady && !errorMessage && (
          <p className="text-muted text-center py-10">
            Pick a branch in the top bar to see its attendance.
          </p>
        )}

        {state.branchReady && !state.authToken && !errorMessage && (
          <p className="text-muted text-center py-10">Signing you in…</p>
        )}

        {state.branchReady &&
          state.authToken &&
          state.dateIsValid &&
          isLoading && (
            <p className="text-muted text-center py-10">Loading attendance…</p>
          )}

        {state.branchReady &&
          state.authToken &&
          !isLoading &&
          !errorMessage &&
          hasLoaded &&
          orderedSessions.length === 0 && (
            <p className="text-muted text-center py-10">
              No sessions were scheduled at this branch on {date}.
            </p>
          )}

        {!isLoading && !errorMessage && orderedSessions.length > 0 && (
          <>
            <div className="d-flex align-items-center flex-wrap mb-5">
              <span className="text-muted font-weight-bold font-size-sm mr-4">
                {orderedSessions.length} session
                {orderedSessions.length === 1 ? "" : "s"} · {dayTotals.present}{" "}
                present · {dayTotals.absent} absent · {dayTotals.unmarked}{" "}
                unmarked
                {dayTotals.scanned > 0
                  ? ` · ${dayTotals.scanned} checked in at the door`
                  : ""}
                {dayTotals.typed > 0 ? ` (${dayTotals.typed} typed)` : ""}
              </span>
              <Button
                className="btn btn-light font-weight-bolder font-size-sm"
                onClick={() =>
                  setExpanded(allOpen ? new Set() : new Set(allKeys))
                }
              >
                {allOpen ? "Collapse all" : "Expand all"}
              </Button>
            </div>

            {orderedSessions.map((session, index) => {
              const key = sessionKey(session, index);
              const students = Array.isArray(session.students)
                ? session.students
                : [];
              const counts = tally(students);
              const open = expanded.has(key);
              return (
                <div className="border-bottom mb-3 pb-3" key={key}>
                  <div
                    className="d-flex align-items-center justify-content-between flex-wrap cursor-pointer"
                    role="button"
                    tabIndex={0}
                    aria-expanded={open}
                    aria-controls={`zr-session-${key}`}
                    onClick={() => toggle(key)}
                    onKeyPress={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        toggle(key);
                      }
                    }}
                  >
                    <span className="font-weight-bolder text-dark">
                      {sessionLabel(session) || `Batch #${session.batch_id}`}
                    </span>
                    <span className="text-muted font-weight-bold font-size-sm">
                      {students.length} student
                      {students.length === 1 ? "" : "s"} · {counts.present}{" "}
                      present · {counts.absent} absent · {counts.unmarked}{" "}
                      unmarked
                      <span className="ml-3">{open ? "▾" : "▸"}</span>
                    </span>
                  </div>

                  <Collapse in={open}>
                    <div id={`zr-session-${key}`}>
                      {students.length === 0 ? (
                        <p className="text-muted mt-3 mb-0">
                          No students are enrolled in this class.
                        </p>
                      ) : (
                        <div className="table-responsive mt-3">
                          <table className="table table-head-custom table-vertical-center">
                            <thead>
                              <tr>
                                <th>Student</th>
                                <th style={{ width: "20%" }}>Attendance</th>
                                <th style={{ width: "30%" }}>Arrival</th>
                              </tr>
                            </thead>
                            <tbody>
                              {[...students]
                                .sort((a, b) =>
                                  studentName(a).localeCompare(studentName(b))
                                )
                                .map((student, studentIndex) => (
                                  <StudentRow
                                    key={
                                      student.user_id ||
                                      `student-${studentIndex}`
                                    }
                                    student={student}
                                  />
                                ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </Collapse>
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}

function StudentRow({ student }) {
  const state = studentState(student);
  const style = STATE_STYLES[state];
  const absent =
    state !== STUDENT_STATES.UNMARKED && student.attendance === "absent";

  return (
    <tr>
      <td className="font-weight-bolder text-dark">
        {studentName(student) || `Student #${student.user_id}`}
        {/*
          A qualifier, kept quiet on purpose. It explains why someone is on a
          roster they have no `student_batch` row for — the kiosk marks make-up
          students Present, and some real scans belong to them — but it says
          nothing about whether they attended. Given a badge of its own it
          would read as a fourth attendance state beside Present/Absent/
          Unmarked, which it is not.
        */}
        {student.makeup ? (
          <span className="text-muted font-weight-normal font-size-sm ml-2">
            make-up
          </span>
        ) : null}
      </td>
      <td>
        <span
          className={
            absent
              ? "label label-lg label-light-danger label-inline"
              : style.badge
          }
        >
          {absent ? "Absent" : style.label}
        </span>
      </td>
      <td>
        {state === STUDENT_STATES.SCANNED ? (
          <span className="font-weight-bold text-dark">
            {formatScanTime(student.scanned_in_at)}
          </span>
        ) : (
          // Never blank. There is no time for these rows and there is not
          // meant to be one — see STATE_STYLES.
          <span className="text-muted font-weight-bold">{style.note}</span>
        )}
      </td>
    </tr>
  );
}
